import { useState, useEffect } from 'react';
import { Send, Mail, Phone, MapPin, CheckCircle, X } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [showPopup, setShowPopup] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Form submission logic would go here
    console.log('Form submitted:', formData);
    setShowPopup(true);
    setFormData({ name: '', email: '', message: '' });
  };

  useEffect(() => {
    if (showPopup) {
      const timer = setTimeout(() => {
        setShowPopup(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showPopup]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-dark-black to-gray-900">
      {/* Success Popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-dark-black border-2 border-toxic-orange rounded-xl p-6 max-w-md w-full shadow-2xl animate-slide-up">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-toxic-orange/20 p-2 rounded-full">
                  <CheckCircle className="text-toxic-orange" size={24} />
                </div>
                <h3 className="text-xl font-bold text-white">Message Sent!</h3>
              </div>
              <button
                onClick={() => setShowPopup(false)}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Close popup"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-gray-300 leading-relaxed">
              Thank you for your message! We'll get back to you soon.
            </p>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Get in Touch
            </h2>
            <p className="text-gray-400 text-lg">
              Have a question or feedback? We'd love to hear from you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">
                Contact Information
              </h3>
              <div className="space-y-6" itemScope itemType="https://schema.org/ContactPoint">
                <div className="flex items-start gap-4">
                  <div className="bg-toxic-orange/20 p-3 rounded-lg">
                    <Mail className="text-toxic-orange" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Email</h4>
                    <p itemProp="email" className="text-gray-300 hover:text-toxic-orange transition-colors">
                      info@empire-wagers.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-toxic-orange/20 p-3 rounded-lg">
                    <Phone className="text-toxic-orange" size={24} />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Phone</h4>
                    <p  itemProp="telephone" className="text-gray-300 hover:text-toxic-orange transition-colors">
                      +44 5511 778 569
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-toxic-orange/20 p-3 rounded-lg">
                    <MapPin className="text-toxic-orange" size={24} />
                  </div>
                  <div itemScope itemType="https://schema.org/PostalAddress">
                    <h4 className="text-white font-semibold mb-1">Address</h4>
                    <p className="text-gray-300">
                      <span itemProp="addressLocality">London</span>, <span itemProp="addressCountry">United Kingdom</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-white font-semibold mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    autoComplete="name"
                    required
                    className="w-full bg-dark-black border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-toxic-orange transition-colors"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-white font-semibold mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                    required
                    className="w-full bg-dark-black border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-toxic-orange transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-white font-semibold mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    autoComplete="off"
                    required
                    rows="5"
                    className="w-full bg-dark-black border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-toxic-orange transition-colors resize-none"
                    placeholder="Your message..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-toxic-orange text-dark-black py-3 rounded-lg font-semibold hover:bg-orange-500 transition-colors flex items-center justify-center group"
                >
                  Send Message
                  <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;

